import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Hammer, Archive, Coins, Users, Scroll, ArrowRight, ShieldCheck,
} from "lucide-react";

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div
    className="glass p-6 relative overflow-hidden hover:gold-glow transition-all duration-500"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="label-tag mb-2">{label}</div>
        <div className="text-4xl font-black tracking-tighter text-white">{value}</div>
      </div>
      <div className="w-10 h-10 flex items-center justify-center border border-[#C9A227]/40">
        <Icon className="w-5 h-5 gold-text" strokeWidth={1.5} />
      </div>
    </div>
    <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-[#C9A227]/5 blur-2xl" />
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [admirals, setAdmirals] = useState([]);

  useEffect(() => {
    Promise.all([api.forgeStats(), api.listContracts(), api.getAdmirals()])
      .then(([s, c, a]) => {
        setStats(s);
        setContracts(c.slice(0, 5));
        setAdmirals(a);
      })
      .catch(console.error);
  }, []);

  return (
    <div className="space-y-12" data-testid="dashboard-page">
      <section className="space-y-4">
        <div className="label-tag">Command Bridge</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white">
          The Armada's <span className="gold-text">Legal Spine</span>.
        </h1>
        <p className="text-gray-400 max-w-2xl text-base">
          Forge contracts, automate handshakes, and archive every binding seal across the EIGOO fleet.
          The Master Ledger streams Admiral data directly into your instruments.
        </p>
        <div className="flex flex-wrap gap-3 pt-2">
          <Link to="/forge" className="btn-gold inline-flex items-center gap-2" data-testid="cta-open-forge">
            Open the Forge <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/archives" className="btn-ghost-gold inline-flex items-center gap-2" data-testid="cta-open-archives">
            <Archive className="w-4 h-4" /> View Archives
          </Link>
        </div>
      </section>

      <div className="divider-gold" />

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
        <StatCard label="Forged" value={stats?.total_contracts ?? "—"} icon={Scroll} testid="stat-total" />
        <StatCard label="Signed" value={stats?.signed_contracts ?? "—"} icon={ShieldCheck} testid="stat-signed" />
        <StatCard label="Admirals" value={stats?.fleet_admirals ?? "—"} icon={Users} testid="stat-admirals" />
        <StatCard label="Licenses Sold" value={stats?.licenses_issued ?? "—"} icon={Coins} testid="stat-licenses" />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-sharp p-6" data-testid="recent-contracts">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-tag">Most Recent</div>
              <h2 className="text-xl font-bold mt-1">Forged Contracts</h2>
            </div>
            <Link to="/archives" className="text-xs label-tag gold-text hover:text-[#E0BC4A]">
              View All →
            </Link>
          </div>
          {contracts.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              No contracts forged yet. <Link to="/forge" className="gold-text underline">Start the Forge.</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {contracts.map((c) => (
                <Link
                  key={c.id}
                  to={`/archives/${c.id}`}
                  className="flex items-center justify-between p-4 border border-white/5 hover:border-[#C9A227]/40 transition-colors group"
                  data-testid={`contract-row-${c.id}`}
                >
                  <div>
                    <div className="font-mono text-xs gold-text">{c.contract_code}</div>
                    <div className="mt-1 font-bold text-sm">{c.template_name}</div>
                    <div className="text-xs text-gray-500">
                      {c.counterparty} · {c.admiral_name}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
                      c.status === "signed"
                        ? "border-emerald-500/40 text-emerald-400"
                        : c.status === "sent_for_signature"
                        ? "border-yellow-500/40 text-yellow-400"
                        : "border-white/10 text-gray-400"
                    }`}>
                      {c.status.replace("_", " ")}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#C9A227] transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-sharp p-6" data-testid="admiral-roster">
          <div className="label-tag">Master Ledger</div>
          <h2 className="text-xl font-bold mt-1 mb-6">Admiral Roster</h2>
          <div className="space-y-3">
            {admirals.slice(0, 6).map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                data-testid={`admiral-row-${a.id}`}
              >
                <div>
                  <div className="font-bold text-sm">{a.admiral_name}</div>
                  <div className="text-xs text-gray-500 mono">{a.vessel_id} · {a.rank}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs label-tag">Gold</div>
                  <div className="font-mono text-sm gold-text">{a.gold_amount.toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
